import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, ArrowLeft, MailCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import api from '../lib/api';

/**
 * ForgotPasswordPage
 * ------------------
 * Public entry-point for the self-service password reset flow.
 * Posts to `/api/auth/request-password-reset` and shows a generic
 * "If an account with that email exists, we sent a link" success
 * card regardless of whether the email matched a real user — the
 * backend never tells us either way (account-enumeration defense).
 *
 * The success card sticks around so users see the same response
 * whether they typed a real address, a typo, or a random string.
 */
export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || submitting) return;
    setSubmitting(true);
    try {
      await api.post('/auth/request-password-reset', { email: email.trim() });
    } catch {
      // Even on a network error we render the generic success
      // state — the worst case is a user retrying after their
      // network recovers, which is fine.
    }
    setSubmitted(true);
    setSubmitting(false);
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-teal-900 flex items-center justify-center p-4"
      data-testid="forgot-password-page"
    >
      <Card className="w-full max-w-md bg-white border border-slate-200 backdrop-blur shadow-2xl">
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center mb-4">
            <div className="h-12 w-12 rounded-full bg-teal-50 flex items-center justify-center">
              {submitted ? (
                <MailCheck className="h-6 w-6 text-teal-600" />
              ) : (
                <Mail className="h-6 w-6 text-teal-600" />
              )}
            </div>
          </div>
          <CardTitle className="text-2xl font-bold text-slate-800">
            {submitted ? 'Check your email' : 'Forgot password'}
          </CardTitle>
          <CardDescription>
            {submitted
              ? 'If an account exists for that email, we just sent a reset link. The link works once and expires in 24 hours.'
              : 'Enter the email you sign in with — we\'ll send you a secure link to choose a new password.'}
          </CardDescription>
        </CardHeader>

        <CardContent>
          {!submitted && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input
                  type="email"
                  placeholder="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 h-12"
                  data-testid="forgot-password-email-input"
                  autoFocus
                  required
                />
              </div>
              <Button
                type="submit"
                className="w-full h-12 bg-teal-600 hover:bg-teal-700"
                disabled={submitting || !email.trim()}
                data-testid="forgot-password-submit-btn"
              >
                {submitting
                  ? <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                  : 'Send reset link'}
              </Button>
            </form>
          )}

          {submitted && (
            <div className="space-y-3 text-sm text-slate-600">
              <p>
                If you don't see the email in a few minutes, check your spam folder.
                Still nothing? Ask your Super Admin or Organization Admin to re-issue an invitation.
              </p>
              <Button
                onClick={() => { setSubmitted(false); setEmail(''); }}
                variant="outline"
                className="w-full"
                data-testid="forgot-password-resubmit-btn"
              >
                Use a different email
              </Button>
            </div>
          )}

          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="inline-flex items-center text-sm font-medium text-teal-700 hover:text-teal-900"
              data-testid="forgot-password-back-to-login"
              onClick={(e) => {
                e.preventDefault();
                navigate('/login');
              }}
            >
              <ArrowLeft className="h-4 w-4 mr-1" /> Back to sign in
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
